import { useEffect, useState } from "react";
import { apiPost } from "components/Common/Axios";
import {
  ClassNames,
  DisplayYears,
  Order,
  Sort,
  StageName,
  StageType,
  UserRole,
} from "lib/types";
import { DescriptionOutlined } from "@mui/icons-material";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { Checkbox } from "components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Label } from "components/ui/label";
import { cn, formatKoreanDate, getMonth, getYear } from "lib/utils";
import { RootState, useAppDispatch, useAppSelector } from "store";
import { setSearchTaxStatus, setTaxYears } from "store/mainSlice";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { Button } from "components/ui/button";
import { School, SchoolSales } from "lib/models";
import SchoolSalesPopup from "../Main/SchoolSalesPopup";
import VatIssueRequestPopup, { IssuedType } from "./VatIssueRequestPopup";
import { Switch } from "components/ui/switch";
import { useAlert } from "components/ui/alert-context";
import toast from "react-hot-toast";
import Stage from "components/Common/stage";

type SchoolSalesCheck = {
  schoolSales: SchoolSales;
  checked: boolean;
};

const displayStages = [5, 6, 7];

// 세금계산서 요청
const VatIssue = () => {
  // [TaxIssueRequestPopup]
  const [openRequest, setOpenRequest] = useState(false);
  const [requestSchoolSales, setRequestSchoolSales] = useState<SchoolSales[]>(
    []
  );
  const [issuedType, setIssuedType] = useState<IssuedType>("total");

  // [SchoolSalesPopup]
  const [openSales, setOpenSales] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<School | undefined>(
    undefined
  );
  const [selectedSchoolSales, setSelectedSchoolSales] = useState<
    SchoolSales | undefined
  >(undefined);

  // [SchoolSalesCheck List]
  // 모든 SchoolSalesCheck
  const [totalSchoolSalesCheck, setTotalSchoolSalesCheck] = useState<
    SchoolSalesCheck[]
  >([]);

  // 검색조건에 Filtering된 (화면에 보이는) SchoolSalesCheck
  const [filteredSalesCheck, setFilteredSalesCheck] = useState<
    SchoolSalesCheck[]
  >([]);

  // filteredSalesCheck에서 체크된것만 모아둔 것
  const [filteredSalesCheckSelected, setFilteredSalesCheckSelected] = useState<
    SchoolSalesCheck[]
  >([]);

  // 모든 Sale선택 체크박스
  const [checkAllSchoolSales, setCheckAllSchoolSales] =
    useState<boolean>(false);

  // 모든 stage 선택 체크박스
  const [checkStageAll, setCheckStageAll] = useState<boolean>(true);

  const [sort, setSort] = useState<Sort>("region");
  const [order, setOrder] = useState<Order>("asc");
  const [searchString, setSearchString] = useState<string>("");
  const [stageFilterStatus, setStageFilterStatus] = useState<number[]>([
    ...displayStages,
  ]);
  const [isShowComplete, setIsShowComplete] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectMonth, setSelectMonth] = useState<number>(0);
  const [isShowFree, setIsShowFree] = useState<boolean>(false);

  const taxYears = useAppSelector((state: RootState) => state.main.taxYears);
  const searchOption = useAppSelector((state) => state.main.searchTaxOption);
  const dispatch = useAppDispatch();
  const { show } = useAlert();

  const me = useAppSelector((state: RootState) => state.user.me);

  // store에 있는 option값 (searchTaxOption)을 가져와서 초기값으로 설정
  useEffect(() => {
    if (searchOption) {
      setSearchString(searchOption.keyword);
      setSort(searchOption.sort as Sort);
      setOrder(searchOption.order as Order);
      setStageFilterStatus(searchOption.status);
      setIsShowComplete(searchOption.isShowComplete);
      setSelectMonth(searchOption.selectMonth);
      setIsShowFree(searchOption.isShowFree);
    }
    loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // searchOption들이 바뀌면 다시 적용
  useEffect(() => {
    // store에 현재상태 저장
    dispatch(
      setSearchTaxStatus({
        keyword: searchString,
        sort,
        order,
        status: stageFilterStatus,
        isShowComplete,
        selectMonth,
        isShowFree,
      })
    );

    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    totalSchoolSalesCheck,
    searchString,
    sort,
    order,
    stageFilterStatus,
    isShowComplete,
    selectMonth,
    isShowFree,
    dispatch,
    taxYears,
  ]);

  // 3개의 stage의 SchoolSales 불러오기
  const loadSales = async () => {
    const response = await apiPost("/school/sales-stages", {
      stages: [
        StageType.CONTRACT_COMPLETED,
        StageType.CLASS_ASSIGNED,
        StageType.CLASS_COMPLETED,
      ],
    });

    if (response.error) {
      toast.error("수업 정보를 불러오는데 실패했습니다.");
      console.error("loadSales error", response.error);
      return;
    }

    const sales = response.data as SchoolSales[];

    // 불러온 후에 schoolSales=> SchoolSalesCheck로 변환 (모든 체크를 false인 상태로)
    setTotalSchoolSalesCheck(
      sales.map((s) => {
        return { schoolSales: s, checked: false };
      })
    );
  };

  // 검색시 현재 검색어에 맞게 필터링
  const search = () => {
    const stages = stageFilterStatus
      .map((f, i) => {
        return { f, stage: i + StageType.CONTRACT_COMPLETED };
      })
      .filter((fi) => fi.f)
      .map((fi) => fi.stage);

    const optionfilteredSales = totalSchoolSalesCheck.filter((s) => {
      return (
        (s.schoolSales.school.name.includes(searchString) ||
          // s.school.sido.name.includes(str) ||
          // s.school.gugun.name.includes(str) ||
          // s.school.address.includes(str) ||
          s.schoolSales.school.phone.includes(searchString) ||
          s.schoolSales.teacherName.includes(searchString) ||
          s.schoolSales.phoneNo.includes(searchString) ||
          s.schoolSales.email.includes(searchString) ||
          s.schoolSales.memo.includes(searchString)) &&
        stages.includes(s.schoolSales.stage) &&
        (isShowComplete || !s.schoolSales.isVatInvoiceRequested)
      );
    });

    if (sort === "class_date") {
      if (order === "asc") {
        optionfilteredSales.sort((a, b) => {
          return a.schoolSales.classDateFrom > b.schoolSales.classDateFrom
            ? 1
            : -1;
        });
      } else {
        optionfilteredSales.sort((a, b) => {
          return a.schoolSales.classDateFrom < b.schoolSales.classDateFrom
            ? 1
            : -1;
        });
      }
    } else if (sort === "contract_date") {
      if (order === "asc") {
        optionfilteredSales.sort((a, b) => {
          return a.schoolSales.contractDate > b.schoolSales.contractDate
            ? 1
            : -1;
        });
      } else {
        optionfilteredSales.sort((a, b) => {
          return a.schoolSales.contractDate < b.schoolSales.contractDate
            ? 1
            : -1;
        });
      }
    } else if (sort === "name") {
      if (order === "asc") {
        optionfilteredSales.sort((a, b) => {
          return a.schoolSales.school.name > b.schoolSales.school.name ? 1 : -1;
        });
      } else {
        optionfilteredSales.sort((a, b) => {
          return a.schoolSales.school.name < b.schoolSales.school.name ? 1 : -1;
        });
      }
    } else if (sort === "region") {
      if (order === "asc") {
        optionfilteredSales.sort((a, b) => {
          return a.schoolSales.school.sido.name +
            a.schoolSales.school.gugun.name +
            a.schoolSales.school.name >
            b.schoolSales.school.sido.name +
              b.schoolSales.school.gugun.name +
              b.schoolSales.school.name
            ? 1
            : -1;
        });
      } else {
        optionfilteredSales.sort((a, b) => {
          return a.schoolSales.school.sido.name +
            a.schoolSales.school.gugun.name +
            a.schoolSales.school.name <
            b.schoolSales.school.sido.name +
              b.schoolSales.school.gugun.name +
              b.schoolSales.school.name
            ? 1
            : -1;
        });
      }
    } else if (sort === "stage") {
      if (order === "asc") {
        optionfilteredSales.sort((a, b) => {
          return a.schoolSales.stage > b.schoolSales.stage ? 1 : -1;
        });
      } else {
        optionfilteredSales.sort((a, b) => {
          return a.schoolSales.stage < b.schoolSales.stage ? 1 : -1;
        });
      }
    }

    const filteredSales = optionfilteredSales.filter(
      (s) =>
        taxYears.includes(getYear(s.schoolSales.classDateFrom)) &&
        (taxYears.length !== 1 ||
          selectMonth === 0 ||
          getMonth(s.schoolSales.classDateFrom) === selectMonth) &&
        !s.schoolSales.isFree
    );

    setFilteredSalesCheck(filteredSales);

    setFilteredSalesCheckSelected(filteredSales.filter((s) => s.checked));
  };

  // Stage의 모두선택 클릭시
  const handleStageCheckAll = async (checked: boolean) => {
    setCheckStageAll(checked);
    if (checked) {
      setStageFilterStatus([...displayStages]);
    } else {
      setStageFilterStatus([]);
    }
  };

  const handleStageCheck = (isChecked: boolean, stage: number) => {
    let newFilters = [];
    if (isChecked) {
      newFilters = [...stageFilterStatus, stage];
    } else {
      newFilters = stageFilterStatus.filter((f) => f !== stage);
    }
    setStageFilterStatus(newFilters);
  };

  // 현재 화면에 표시된 모든 SchoolSales를 체크
  const handleCheckFilteredSchoolSalesAll = async (checked: boolean) => {
    setCheckAllSchoolSales(checked);

    setFilteredSalesCheck(
      filteredSalesCheck.map((c: SchoolSalesCheck) => {
        return { ...c, checked };
      })
    );

    const checkedSchoolSalesIds = filteredSalesCheck.map(
      (s) => s.schoolSales.id
    );
    setTotalSchoolSalesCheck(
      totalSchoolSalesCheck.map((c) => {
        return {
          ...c,
          checked: checkedSchoolSalesIds.includes(c.schoolSales.id)
            ? checked
            : c.checked,
        };
      })
    );
  };

  const handleIsShowComplete = (checked: boolean) => {
    setIsShowComplete(checked);
  };

  // SchoolSales 체크클릭
  const handleSchoolSalesCheckChange = (checked: boolean, index: number) => {
    let checkedSchoolSalesId = 0;

    setFilteredSalesCheck(
      filteredSalesCheck.map((c, i) => {
        if (i === index) {
          checkedSchoolSalesId = c.schoolSales.id;
          return { ...c, checked };
        } else {
          return c;
        }
      })
    );

    setTotalSchoolSalesCheck(
      totalSchoolSalesCheck.map((c) => {
        if (c.schoolSales.id === checkedSchoolSalesId) {
          return { ...c, checked };
        } else {
          return c;
        }
      })
    );
  };

  // 세금계산서 발급요청
  const handleRequest = async () => {
    let selectedSales = [];

    selectedSales = totalSchoolSalesCheck.filter((s, i) => s.checked);
    if (selectedSales.length === 0) {
      show("오류", "발급요청할 계약을 선택하세요", "alert");
      return;
    }

    selectedSales = totalSchoolSalesCheck.filter(
      (s, i) => s.checked && !s.schoolSales.isVatInvoiceRequested
    );

    if (selectedSales.length === 0) {
      show("오류", "이미 발급을 완료한 계약입니다.", "alert");
      return;
    }

    setRequestSchoolSales(selectedSales.map((s) => s.schoolSales));
    setIsEditMode(false);
    setIssuedType("new");
    setOpenRequest(true);
  };

  const popupSchoolSales = (e: any, sales: SchoolSales) => {
    if (e.target.closest("a")) {
      return;
    }
    setSelectedSchool(sales.school);
    setSelectedSchoolSales(sales);
    setOpenSales(true);
  };

  const onSaveSchoolSalesCompleted = async (
    schoolSales: SchoolSales,
    isNewMode: boolean
  ) => {
    setTotalSchoolSalesCheck(
      totalSchoolSalesCheck.map((schoolSalesCheck) => {
        if (schoolSalesCheck.schoolSales.id === schoolSales.id) {
          schoolSalesCheck.schoolSales = schoolSales;
        }
        return schoolSalesCheck;
      })
    );

    return true;
  };

  // 이미 작성된 요청 수정하기
  const handleEditIssueLectureRequest = async (
    schoolSales: SchoolSales,
    issuedType: IssuedType
  ) => {
    const reqSchoolSales = totalSchoolSalesCheck
      .map((schoolSalesCheck) => schoolSalesCheck.schoolSales)
      .filter((totalSchoolSales) => {
        // 강사비 재료비를 한개의 세금계산서로 발행한 경우
        if (issuedType === "total") {
          return (
            (schoolSales.vatInvoice
              ? schoolSales.vatInvoice.schoolSales.some((vatSchoolSales) => {
                  return vatSchoolSales.id === totalSchoolSales.id;
                })
              : false) ||
            (schoolSales.vatInvoice
              ? schoolSales.vatInvoiceMaterial?.schoolSalesMaterial.some(
                  (vatSchoolSales) => {
                    return vatSchoolSales.id === totalSchoolSales.id;
                  }
                )
              : false)
          );
        } else if (issuedType === "lecture") {
          // 강사비만 한개의 세금계산서로 발행한 경우
          return (
            schoolSales.vatInvoice?.schoolSales.some((vatSchoolSales) => {
              return vatSchoolSales.id === totalSchoolSales.id;
            }) && totalSchoolSales.isIssuedLecture
          );
        } else {
          // 재료비만 한개의 세금계산서로 발행한 경우
          return (
            schoolSales.vatInvoiceMaterial?.schoolSalesMaterial.some(
              (vatSchoolSales) => {
                return vatSchoolSales.id === totalSchoolSales.id;
              }
            ) && totalSchoolSales.isIssuedMaterial
          );
        }
      });

    setRequestSchoolSales([...reqSchoolSales]);
    setIssuedType(issuedType);
    setOpenRequest(true);
  };

  const handleYearClick = (year: number) => {
    if (taxYears.includes(year)) {
      dispatch(setTaxYears(taxYears.filter((y) => y !== year)));
    } else {
      dispatch(setTaxYears([...taxYears, year]));
    }
  };

  return (
    <>
      <SchoolSalesPopup
        open={openSales}
        onOpenChange={(v) => setOpenSales(v)}
        schoolSales={selectedSchoolSales!}
        school={selectedSchool!}
        onClose={() => setOpenSales(false)}
        onSaveCompleted={onSaveSchoolSalesCompleted}
        onDelete={async (sales: SchoolSales) => {
          return true;
        }}
        isEnableNew={false}
        isShowDelete={true}
        accordionStates={["item-3"]}
      />

      <VatIssueRequestPopup
        open={openRequest}
        onOpenChange={(v) => setOpenRequest(v)}
        schoolSalesList={requestSchoolSales}
        setRequestSchoolSales={setRequestSchoolSales}
        isEditMode={isEditMode}
        reload={loadSales}
        issuedType={issuedType}
      />

      <div className="text-sm pb-8">
        <div className="flex flex-col xl:flex-row xl:justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50/70 px-4 py-3 mb-4">
          <div className="flex flex-wrap items-center gap-2 pb-2 xl:pb-4">
            <div>정렬</div>
            <Select value={sort} onValueChange={(v) => setSort(v as Sort)}>
              <SelectTrigger className="w-[120px] text-center">
                <SelectValue placeholder="1" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="contract_date">계약일</SelectItem>
                  <SelectItem value="class_date">수업일</SelectItem>
                  <SelectItem value="name">이름</SelectItem>
                  <SelectItem value="region">지역</SelectItem>
                  <SelectItem value="stage">단계</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
            <Select value={order} onValueChange={(v) => setOrder(v as Order)}>
              <SelectTrigger className="w-[120px] text-center">
                <SelectValue placeholder="1" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="asc">오름차순</SelectItem>
                  <SelectItem value="desc">내림차순</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>

            <div>검색</div>
            <input
              className="px-2 py-2 border border-gray-200 rounded-md w-[140px] bg-white"
              type="text"
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
            />

            <div className="flex flex-wrap items-center gap-3 xl:pl-4">
              <div className="flex  items-center gap-1">
                <Checkbox
                  id="checkAll"
                  className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                  checked={checkStageAll}
                  onCheckedChange={(v: boolean) => handleStageCheckAll(v)}
                />

                <Label htmlFor="checkAll">전체</Label>
              </div>
              {displayStages.map((stage, index) => (
                <div key={index} className="flex  items-center gap-1">
                  <Checkbox
                    id={`id${index}`}
                    className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                    checked={stageFilterStatus.includes(stage)}
                    onCheckedChange={(checked: boolean) =>
                      handleStageCheck(checked, stage)
                    }
                  />
                  <Label htmlFor={`id${index}`}>
                    {StageName[index + StageType.CONTRACT_COMPLETED]}
                  </Label>
                </div>
              ))}
            </div>

            <div className="xl:pl-4 flex items-center gap-2">
              <Switch
                checked={isShowComplete}
                onCheckedChange={handleIsShowComplete}
                id="isShowComplete"
              />
              <Label htmlFor="isShowComplete">발급요청완료 포함</Label>
            </div>
            <div className="flex flex-wrap items-center gap-2 xl:px-2 xl:pl-4">
              {DisplayYears.map((year) => (
                <div
                  key={year}
                  className={cn(
                    "text-xs rounded-md py-2 px-3 cursor-pointer w-[80px] text-center",
                    taxYears.includes(year)
                      ? "bg-indigo-600 text-white border border-indigo-600 shadow-sm"
                      : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                  )}
                  onClick={() => handleYearClick(year)}
                >
                  {year}년
                </div>
              ))}
            </div>
          </div>
          <Button onClick={handleRequest} variant="primary" className="w-full xl:w-auto shrink-0">
            세금계산서 발급요청
          </Button>
        </div>
        {taxYears.length === 1 && (
          <div className="flex flex-wrap justify-center items-center gap-2 py-4">
            <div
              className={cn(
                "w-12 h-8 rounded-sm border-solid border border-gray-300 cursor-pointer flex items-center justify-center",
                selectMonth === 0
                  ? "bg-indigo-600 text-white border border-indigo-600 shadow-sm"
                  : "text-gray-800 hover:bg-gray-50"
              )}
              onClick={() => setSelectMonth(0)}
            >
              전체
            </div>
            {Array.from({ length: 12 }, (_, i) => (
              <div
                className={cn(
                  "w-8 h-8 rounded-sm border-solid border border-gray-300 cursor-pointer flex items-center justify-center",
                  selectMonth === i + 1
                    ? "bg-indigo-600 text-white border border-indigo-600 shadow-sm"
                    : "text-gray-800 hover:bg-gray-50"
                )}
                onClick={() => setSelectMonth(i + 1)}
              >
                <div>{i + 1}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-between items-center pb-2 px-2">
          <div>
            선택: {filteredSalesCheck.filter((f) => f.checked).length}개
          </div>
          <div>전체: {filteredSalesCheck.length}개 </div>
        </div>

        <div className="overflow-x-auto">
        <div className="min-w-[1400px]">
        <div className="grid grid-cols-[120px_40px_70px_80px_80px_1fr_1fr_1fr_1fr_1fr_60px_60px_1fr_90px_2fr] border-solid border-b border-t border-gray-200 py-2 bg-gray-50 text-gray-500 font-medium pl-4 text-center">
          <div className="flex items-center gap-x-2">
            <Checkbox
              id="checkAll"
              className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
              checked={checkAllSchoolSales}
              onCheckedChange={handleCheckFilteredSchoolSalesAll}
            />
            <div className="text-center w-full">단계</div>
          </div>
          <div>번호</div>
          <div>SID</div>
          <div>요청상태</div>
          <div>영업자</div>
          <div>학교이름</div>
          <div>계약일시</div>
          <div>수업일시</div>
          <div>발급일시</div>

          <div>수업</div>
          <div>교시</div>
          <div>학급수</div>
          <div className="text-right pr-8">매출</div>
          <div className="text-center">매출입력</div>
          <div>메모</div>
        </div>
        <div className="text-center">
          {filteredSalesCheck
            // .filter(
            //   (s) =>
            //     taxYears.includes(getYear(s.schoolSales.classDateFrom)) &&
            //     (taxYears.length !== 1 ||
            //       selectMonth === 0 ||
            //       getMonth(s.schoolSales.classDateFrom) === selectMonth)
            // )
            .map((s: SchoolSalesCheck, index: number) => (
              <div
                key={s.schoolSales.id}
                onClick={(e) => popupSchoolSales(e, s.schoolSales)}
                className={cn(
                  "grid grid-cols-[120px_40px_70px_80px_80px_1fr_1fr_1fr_1fr_1fr_60px_60px_1fr_90px_2fr] border-solid border-b border-gray-200 pl-4 py-2 items-center cursor-pointer",
                  s.checked
                    ? "bg-indigo-50 hover:bg-indigo-100"
                    : "bg-white hover:bg-gray-50"
                )}
              >
                <div className="flex items-center gap-x-2">
                  <Checkbox
                    className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                    checked={s.checked}
                    onCheckedChange={(checked: boolean) =>
                      handleSchoolSalesCheckChange(checked, index)
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  />
                  {/* <div
                    className={cn(
                      "px-4 py-1 text-white",
                      s.schoolSales.stage === StageType.CONTRACT_COMPLETED
                        ? "bg-[#0dcd63]"
                        : s.schoolSales.stage === StageType.CLASS_ASSIGNED
                        ? "bg-[#008c13]"
                        : s.schoolSales.stage === StageType.CLASS_COMPLETED
                        ? "bg-[#878787]"
                        : "bg-[#454545]"
                    )}
                  >
                    {StageName[s.schoolSales.stage]}
                  </div> */}
                  <Stage
                    stage={s.schoolSales.stage}
                    isFree={s.schoolSales.isFree}
                  />
                </div>
                <div>{index + 1}</div>
                <div>{s.schoolSales.id}</div>
                <div>
                  <div className="flex items-center gap-x-1 justify-center">
                    {/* 강사료+재료비를 한번에 발행한 경우 */}
                    {!s.schoolSales.isIssuedSeparate &&
                    s.schoolSales.isVatInvoiceRequested ? (
                      <div
                        className="bg-cyan-500 rounded-md w-12 h-6 text-center text-white text-sm pt-[2px]"
                        onClick={(e) => {
                          // 세금계산서 발급요청 편집
                          e.stopPropagation();
                          setIsEditMode(true);
                          handleEditIssueLectureRequest(s.schoolSales, "total");
                        }}
                      >
                        전체
                      </div>
                    ) : (
                      <>
                        {s.schoolSales.revenueLecture > 0 && (
                          <div>
                            {s.schoolSales.isIssuedLecture ? (
                              <div
                                className="bg-cyan-500 rounded-md w-6 h-6 text-center text-white text-sm pt-[2px]"
                                onClick={(e) => {
                                  // 세금계산서 발급요청 편집
                                  e.stopPropagation();
                                  setIsEditMode(true);
                                  handleEditIssueLectureRequest(
                                    s.schoolSales,
                                    "lecture"
                                  );
                                }}
                              >
                                강
                              </div>
                            ) : (
                              <div
                                className="bg-white border border-solid rounded-md w-6 h-6 text-center text-gray-500 text-sm pt-[2px]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                강
                              </div>
                            )}
                          </div>
                        )}
                        {s.schoolSales.revenueMaterial > 0 && (
                          <div>
                            {s.schoolSales.isIssuedMaterial ? (
                              <div
                                className="bg-cyan-500 rounded-md w-6 h-6 text-center text-white text-sm pt-[2px]"
                                onClick={(e) => {
                                  // 세금계산서 발급요청 편집
                                  e.stopPropagation();
                                  setIsEditMode(true);
                                  handleEditIssueLectureRequest(
                                    s.schoolSales,
                                    "material"
                                  );
                                }}
                              >
                                재
                              </div>
                            ) : (
                              <div
                                className="bg-white border border-solid rounded-md w-6 h-6 text-center text-gray-500 text-sm pt-[2px]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                재
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div>{s.schoolSales.user.name}</div>
                <div>
                  <Link
                    to={s.schoolSales.school.homepage}
                    target="_blank"
                    className="text-indigo-600 underline"
                  >
                    {s.schoolSales.school.name}{" "}
                  </Link>
                </div>

                <div>
                  {s.schoolSales.contractDate
                    ? formatKoreanDate(s.schoolSales.contractDate)
                    : ""}
                </div>
                <div>
                  {s.schoolSales.classDateFrom
                    ? formatKoreanDate(s.schoolSales.classDateFrom)
                    : ""}
                </div>
                <div>
                  {s.schoolSales.vatInvoice?.issuedDate
                    ? formatKoreanDate(s.schoolSales.vatInvoice?.issuedDate)
                    : ""}
                </div>
                <div>{ClassNames[s.schoolSales.classType - 1]}</div>
                <div>{s.schoolSales.classTimes}</div>

                <div>{s.schoolSales.contractCnt}</div>
                <div className="text-right pr-8">
                  {s.schoolSales.stage >= StageType.CONTRACT_COMPLETED &&
                    s.schoolSales.revenue.toLocaleString()}
                </div>
                <div className="flex justify-center">
                  {s.schoolSales.isRevenueInputCompleted && (
                    <div className="bg-red-400 text-white w-6 h-6 rounded-md pt-[2px] text-center">
                      완
                    </div>
                  )}
                </div>
                {s.schoolSales.memo.length > 0 && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                          <DescriptionOutlined fontSize="small" />{" "}
                          {s.schoolSales.memo}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="w-full">
                        <div className="bg-white border-solid border border-indigo-500  whitespace-break-spaces text-wrap max-w-[500px] text-gray-800 p-4">
                          {s.schoolSales.memo}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            ))}
          </div>
          </div>
          {me?.role === UserRole.ADMIN && (
            <div className="w-full lg:w-[80%] mx-auto my-4 overflow-x-auto">
              <div className="min-w-[800px]">
              <div className="grid grid-cols-[1fr_1fr_2fr_2fr_2fr_2fr_2fr] gap-2 p-4 border border-solid border-gray-300 text-right">
                <div />
                <div>갯수</div>
                <div>매출총액</div>
                <div>공급액 총액</div>
                <div>부가세액 총액</div>
                <div>지출총액</div>
                <div>수익총액</div>
              </div>

              <div className="grid grid-cols-[1fr_1fr_2fr_2fr_2fr_2fr_2fr] gap-2 p-4 border-l border-r border-b border-solid border-gray-300 ">
                <div>총액</div>
                <div className="text-right">
                  {filteredSalesCheck.length.toLocaleString()}
                </div>
                <div className="text-right">
                  {filteredSalesCheck
                    .reduce((acc, cur) => acc + cur.schoolSales.revenue, 0)
                    .toLocaleString()}
                  원
                </div>
                <div className="text-right">
                  {filteredSalesCheck
                    .reduce(
                      (acc, cur) =>
                        acc +
                        Math.round(
                          cur.schoolSales.revenue > 1
                            ? (cur.schoolSales.revenue / 11) * 10
                            : 0
                        ),
                      0
                    )
                    .toLocaleString()}
                  원
                </div>
                <div className="text-right">
                  {" "}
                  {filteredSalesCheck
                    .reduce(
                      (acc, cur) =>
                        acc +
                        Math.round(
                          cur.schoolSales.revenue > 1
                            ? cur.schoolSales.revenue / 11
                            : 0
                        ),
                      0
                    )
                    .toLocaleString()}
                  원
                </div>
                <div className="text-right">
                  {filteredSalesCheck
                    .reduce((acc, cur) => acc + cur.schoolSales.cost, 0)
                    .toLocaleString()}
                  원
                </div>
                <div className="text-right">
                  {" "}
                  {(
                    filteredSalesCheck.reduce(
                      (acc, cur) =>
                        acc + (cur.schoolSales.revenue - cur.schoolSales.cost),
                      0
                    ) -
                    filteredSalesCheck.reduce(
                      (acc, cur) =>
                        acc +
                        Math.round(
                          cur.schoolSales.revenue > 1
                            ? cur.schoolSales.revenue / 11
                            : 0
                        ),
                      0
                    )
                  ).toLocaleString()}
                  원
                </div>
              </div>
              <div className="grid grid-cols-[1fr_1fr_2fr_2fr_2fr_2fr_2fr] gap-2 p-4 border-l border-r border-b border-solid border-gray-300 ">
                <div>선택항목</div>
                <div className="text-right">
                  {filteredSalesCheckSelected.length.toLocaleString()}
                </div>
                <div className="text-right">
                  {filteredSalesCheckSelected
                    .reduce((acc, cur) => acc + cur.schoolSales.revenue, 0)
                    .toLocaleString()}
                  원
                </div>
                <div className="text-right">
                  {filteredSalesCheckSelected
                    .reduce(
                      (acc, cur) =>
                        acc +
                        Math.round(
                          cur.schoolSales.revenue > 1
                            ? (cur.schoolSales.revenue / 11) * 10
                            : 0
                        ),
                      0
                    )
                    .toLocaleString()}
                  원
                </div>
                <div className="text-right">
                  {" "}
                  {filteredSalesCheckSelected
                    .reduce(
                      (acc, cur) =>
                        acc +
                        Math.round(
                          cur.schoolSales.revenue > 1
                            ? cur.schoolSales.revenue / 11
                            : 0
                        ),
                      0
                    )
                    .toLocaleString()}
                  원
                </div>
                <div className="text-right">
                  {filteredSalesCheckSelected
                    .reduce((acc, cur) => acc + cur.schoolSales.cost, 0)
                    .toLocaleString()}
                  원
                </div>
                <div className="text-right">
                  {" "}
                  {(
                    filteredSalesCheckSelected.reduce(
                      (acc, cur) =>
                        acc + (cur.schoolSales.revenue - cur.schoolSales.cost),
                      0
                    ) -
                    filteredSalesCheckSelected.reduce(
                      (acc, cur) =>
                        acc +
                        Math.round(
                          cur.schoolSales.revenue > 1
                            ? cur.schoolSales.revenue / 11
                            : 0
                        ),
                      0
                    )
                  ).toLocaleString()}
                  원
                </div>
              </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VatIssue;
